
import { useState, useEffect } from "react"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Progress } from "@/components/ui/progress"
import { Plus, AlertCircle, Wallet, Edit2, Loader2, Check } from "lucide-react"
import { NotificationBell } from "@/components/notification-bell"
import { useAuth } from "@/lib/auth/auth-context"
import { useAccountSelection } from "@/lib/context/account-selection-context"
import { fetchUserCategories } from "@/lib/services/categories"
import { getBudgetsWithSpending, upsertCategoryBudget } from "@/lib/services/budgets"
import type { CustomCategory, CategoryBudgetWithSpending } from "@/lib/types"
import { toast } from "sonner"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"

interface BudgetProps {
  onNavigate?: (view: string) => void
}

export function Budget({ onNavigate }: BudgetProps = {}) {
  const { user } = useAuth()
  const { selectedAccountId, setSelectedAccountId, subAccounts, loading: loadingAccounts } = useAccountSelection()
  const [loading, setLoading] = useState(true)
  const [categories, setCategories] = useState<CustomCategory[]>([])
  const [budgetsWithSpending, setBudgetsWithSpending] = useState<CategoryBudgetWithSpending[]>([])
  const [showAddDialog, setShowAddDialog] = useState(false)
  const [editingCategory, setEditingCategory] = useState<string | null>(null)
  const [budgetAmount, setBudgetAmount] = useState("")
  const [budgetPeriod, setBudgetPeriod] = useState<'weekly' | 'monthly' | 'yearly'>('monthly')
  const [saving, setSaving] = useState(false)

  // Load categories and budgets (filtered by selected account)
  useEffect(() => {
    const loadData = async () => {
      if (!user) return

      try {
        setLoading(true)
        // Fetch expense categories
        const expenseCategories = await fetchUserCategories(user.id, 'expense')
        setCategories(expenseCategories)

        // Fetch budgets with spending, filtered by selected account
        const accountFilter = selectedAccountId === "all" ? undefined : selectedAccountId
        const budgets = await getBudgetsWithSpending(user.id, accountFilter)
        setBudgetsWithSpending(budgets)
      } catch (error) {
        console.error('Error loading budget data:', error)
        toast.error('Failed to load budget data')
      } finally {
        setLoading(false)
      }
    }

    loadData()
  }, [user, selectedAccountId])

  // Calculate totals
  const totalBudget = budgetsWithSpending.reduce((sum, b) => sum + b.budgetAmount, 0)
  const totalSpent = budgetsWithSpending.reduce((sum, b) => sum + b.spent, 0)
  const totalRemaining = totalBudget - totalSpent
  const totalPercentage = totalBudget > 0 ? (totalSpent / totalBudget) * 100 : 0

  // Handle adding/editing budget
  const handleSaveBudget = async () => {
    if (!user || !editingCategory) return

    const amount = parseFloat(budgetAmount)
    if (isNaN(amount) || amount <= 0) {
      toast.error('Please enter a valid amount')
      return
    }

    try {
      setSaving(true)
      // Auto-assign to selected account (null for "all" accounts means global budget)
      const accountId = selectedAccountId === "all" ? null : selectedAccountId
      await upsertCategoryBudget(user.id, editingCategory, amount, budgetPeriod, accountId)

      // Reload budgets with current filter
      const accountFilter = selectedAccountId === "all" ? undefined : selectedAccountId
      const budgets = await getBudgetsWithSpending(user.id, accountFilter)
      setBudgetsWithSpending(budgets)

      toast.success('Budget saved successfully')
      setShowAddDialog(false)
      setEditingCategory(null)
      setBudgetAmount("")
      setBudgetPeriod('monthly')
    } catch (error) {
      console.error('Error saving budget:', error)
      toast.error('Failed to save budget')
    } finally {
      setSaving(false)
    }
  }

  // Open dialog for adding/editing budget
  const openBudgetDialog = (category: string, currentAmount?: number, currentPeriod?: 'weekly' | 'monthly' | 'yearly') => {
    setEditingCategory(category)
    setBudgetAmount(currentAmount?.toString() || "")
    setBudgetPeriod(currentPeriod || 'monthly')
    setShowAddDialog(true)
  }

  // Get selected category details
  const selectedCategory = categories.find(cat => cat.name === editingCategory)
  const existingBudget = budgetsWithSpending.find(b => b.category === editingCategory)

  // Get categories without budgets
  const categoriesWithoutBudgets = categories.filter(
    (cat) => !budgetsWithSpending.find((b) => b.category === cat.name)
  )

  return (
    <div className="space-y-6 p-4 pb-24">
      <div className="max-w-md mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold">Budget</h1>
            <p className="text-sm text-muted-foreground mt-1">Manage your spending limits</p>
          </div>
          <div className="flex items-center gap-2">
            {/* Account Switcher */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="gap-2">
                  <Wallet className="h-4 w-4" />
                  <span className="hidden sm:inline">
                    {selectedAccountId === "all"
                      ? "All Accounts"
                      : subAccounts.find(a => a.id === selectedAccountId)?.name || "Account"}
                  </span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Filter by Account</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => setSelectedAccountId("all")}
                  className="cursor-pointer"
                >
                  <span className="flex items-center justify-between w-full">
                    <span>All Accounts</span>
                    {selectedAccountId === "all" && <Check className="h-4 w-4 ml-2" />}
                  </span>
                </DropdownMenuItem>
                {subAccounts.map((account) => (
                  <DropdownMenuItem
                    key={account.id}
                    onClick={() => setSelectedAccountId(account.id)}
                    className="cursor-pointer"
                  >
                    <span className="flex items-center justify-between w-full">
                      <span>{account.icon} {account.name}</span>
                      {selectedAccountId === account.id && <Check className="h-4 w-4 ml-2" />}
                    </span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <NotificationBell />
          </div>
        </div>

        {loading ? (
          <Card className="p-12 flex items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </Card>
        ) : (
          <>
            {/* Overall Budget Card */}
            {budgetsWithSpending.length > 0 && (
              <Card className="p-6 bg-gradient-to-br from-primary to-primary/80 text-primary-foreground">
                <div className="space-y-4">
                  <div>
                    <p className="text-sm opacity-90">Total Monthly Budget</p>
                    <h2 className="text-3xl font-bold mt-1">${totalBudget.toFixed(2)}</h2>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-semibold">Spent: ${totalSpent.toFixed(2)}</span>
                      <span>Remaining: ${totalRemaining.toFixed(2)}</span>
                    </div>
                    <Progress value={totalPercentage} className="h-3 bg-primary-foreground/20" indicatorClassName={totalPercentage >= 100 ? "bg-gradient-to-r from-red-400 to-red-500" : "bg-gradient-to-r from-emerald-400 to-emerald-600"} />
                  </div>

                  <div className="flex items-center gap-2 text-sm opacity-90">
                    <span>{totalPercentage.toFixed(0)}% of budget used</span>
                  </div>
                </div>
              </Card>
            )}

            {/* Budget Categories */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-base font-semibold">Budget by Category</h3>
                {categoriesWithoutBudgets.length > 0 && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="gap-2"
                    onClick={() => {
                      if (categoriesWithoutBudgets.length > 0) {
                        openBudgetDialog(categoriesWithoutBudgets[0].name)
                      }
                    }}
                  >
                    <Plus className="h-4 w-4" />
                    Add Budget
                  </Button>
                )}
              </div>

              {budgetsWithSpending.length === 0 ? (
                <Card className="p-8 text-center">
                  <Wallet className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
                  <p className="text-muted-foreground mb-4">No budgets set yet</p>
                  <Button onClick={() => {
                    if (categoriesWithoutBudgets.length > 0) {
                      openBudgetDialog(categoriesWithoutBudgets[0].name)
                    } else {
                      toast.error('No categories available. Create categories first.')
                    }
                  }}>
                    <Plus className="h-4 w-4 mr-2" />
                    Create Your First Budget
                  </Button>
                </Card>
              ) : (
                budgetsWithSpending.map((budget, index) => (
                  <Card key={index} className="p-5">
                    <div className="space-y-3">
                      <div className="flex items-center gap-3">
                        <div className={`h-12 w-12 rounded-xl ${budget.color} flex items-center justify-center text-2xl flex-shrink-0`}>
                          {budget.icon}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-1">
                            <h4 className="font-semibold">{budget.category}</h4>
                            <div className="flex items-center gap-2">
                              {budget.percentage > 90 && <AlertCircle className="h-4 w-4 text-destructive" />}
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => openBudgetDialog(budget.category, budget.budgetAmount, budget.period)}
                              >
                                <Edit2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                          <p className="text-sm text-muted-foreground">
                            <span className="text-expense font-medium">${budget.spent.toFixed(2)}</span> of ${budget.budgetAmount.toFixed(2)}
                          </p>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Progress
                          value={budget.percentage}
                          className={`h-2 ${budget.percentage > 90 ? "[&>div]:bg-destructive" : ""}`}
                        />
                        <div className="flex items-center justify-between text-xs text-muted-foreground">
                          <span>{budget.percentage.toFixed(0)}% used</span>
                          <span>
                            {budget.remaining >= 0
                              ? `$${budget.remaining.toFixed(2)} left`
                              : `Over by $${Math.abs(budget.remaining).toFixed(2)}`
                            }
                          </span>
                        </div>
                      </div>

                      {budget.percentage >= 100 ? (
                        <div className="flex items-center gap-2 text-xs text-destructive bg-destructive/10 p-2 rounded-lg">
                          <AlertCircle className="h-3 w-3" />
                          <span>You've exceeded your budget!</span>
                        </div>
                      ) : budget.percentage > 90 ? (
                        <div className="flex items-center gap-2 text-xs text-destructive bg-destructive/10 p-2 rounded-lg">
                          <AlertCircle className="h-3 w-3" />
                          <span>You're close to exceeding your budget!</span>
                        </div>
                      ) : null}
                    </div>
                  </Card>
                ))
              )}
            </div>

            {/* Budget Tips */}
            {budgetsWithSpending.some(b => b.percentage > 80) && (
              <Card className="p-5 bg-accent/5 border-accent/20">
                <div className="flex gap-3">
                  <div className="text-2xl">💡</div>
                  <div className="flex-1">
                    <h4 className="font-semibold mb-1">Budget Tip</h4>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      You're approaching your limit in {budgetsWithSpending.filter(b => b.percentage > 80).length} {budgetsWithSpending.filter(b => b.percentage > 80).length === 1 ? 'category' : 'categories'}.
                      Consider reviewing your expenses to stay within budget.
                    </p>
                  </div>
                </div>
              </Card>
            )}
          </>
        )}
      </div>

      {/* Add/Edit Budget Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <div className="flex items-center gap-3">
              {selectedCategory && (
                <div className={`h-12 w-12 rounded-xl ${selectedCategory.color} flex items-center justify-center text-2xl flex-shrink-0`}>
                  {selectedCategory.icon}
                </div>
              )}
              <div>
                <DialogTitle className="text-xl">
                  {existingBudget ? 'Edit' : 'Set'} Budget
                </DialogTitle>
                <DialogDescription>
                  {existingBudget
                    ? `Update spending limit for ${editingCategory}`
                    : `Set a spending limit for ${editingCategory || 'your category'}`
                  }
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Category Selection - Only show when adding new budget */}
            {!existingBudget && (
              <div className="space-y-3">
                <Label className="text-sm font-medium">Choose Category</Label>
                <div className="grid grid-cols-4 gap-2.5 max-h-[240px] overflow-y-auto p-3 bg-muted/20 rounded-lg border border-muted/40">
                  {categoriesWithoutBudgets.map((cat) => (
                    <button
                      key={cat.id}
                      onClick={() => setEditingCategory(cat.name)}
                      className={`flex flex-col items-center gap-1.5 p-2 rounded-xl border-2 transition-all ${
                        editingCategory === cat.name
                          ? 'border-primary bg-primary/5 scale-105'
                          : 'border-transparent bg-muted/30 hover:bg-muted/50'
                      }`}
                    >
                      <div className={`h-8 w-8 rounded-lg ${cat.color} flex items-center justify-center text-base`}>
                        {cat.icon}
                      </div>
                      <span className="text-xs font-medium text-center line-clamp-1">{cat.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Current Spending Context */}
            {existingBudget && (
              <Card className="p-4 bg-muted/30 border-muted">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Current spending:</span>
                  <span className="font-semibold">${existingBudget.spent.toFixed(2)}</span>
                </div>
              </Card>
            )}

            {/* Period Selection */}
            <div className="space-y-3">
              <Label className="text-sm font-medium">Budget Period</Label>
              <div className="grid grid-cols-3 gap-2">
                {(['weekly', 'monthly', 'yearly'] as const).map((period) => (
                  <Button
                    key={period}
                    type="button"
                    variant={budgetPeriod === period ? 'default' : 'outline'}
                    onClick={() => setBudgetPeriod(period)}
                    className="capitalize"
                  >
                    {period}
                  </Button>
                ))}
              </div>
            </div>

            {/* Amount Input */}
            <div className="space-y-3">
              <Label htmlFor="amount" className="text-sm font-medium">
                Budget Amount
              </Label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-2xl font-bold text-muted-foreground">
                  $
                </span>
                <Input
                  id="amount"
                  type="number"
                  placeholder="0.00"
                  value={budgetAmount}
                  onChange={(e) => setBudgetAmount(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && budgetAmount && editingCategory) {
                      handleSaveBudget()
                    }
                  }}
                  className="pl-12 text-2xl font-bold h-16 text-center"
                  step="0.01"
                  min="0"
                  autoFocus
                />
              </div>
              <p className="text-xs text-muted-foreground text-center">
                Set your {budgetPeriod} spending limit
              </p>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setShowAddDialog(false)
                setEditingCategory(null)
                setBudgetAmount("")
                setBudgetPeriod('monthly')
              }}
              disabled={saving}
              className="flex-1 sm:flex-none"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveBudget}
              disabled={saving || !editingCategory || !budgetAmount}
              className="flex-1 sm:flex-none"
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Saving...
                </>
              ) : (
                <>
                  <Wallet className="h-4 w-4 mr-2" />
                  Save Budget
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
